import React, { useContext, useEffect, useState } from 'react'
import { Button, DangerButton, DataBlock, ResponsiveTableRow, Separator } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { getSessionId } from '@fs/zion-session'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { usePageView } from '@fs/zion-analytics'
import { DmcResponsiveTable } from '../../../../common/DmcResponsiveTable'
import { DatsJobBaseContext } from '../DatsJobContext'
import { useResultsQueriesList } from './useResultsQueriesList'
import { getBaseUrl } from '../../../../../services/toolsService'
import { CreateResultQuery } from './createResultsQuery/createResultQuery'
import { convertUTCString } from '../../../../../util/DateUtil'
import { UserLink } from '../../../../app/Links'
import getUserSummary from '../../../../../services/userService'
import { CIS_USER_ID_CONTEXT } from '../../../../app/LookupContext'
import { useCreateResultQuery } from './createResultsQuery/useCreateResultQuery'
import ToolsButtons from '../ToolsButtons'
import { usePageTitle } from '../../../../app/PageTitleContext'

/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-array-index-key */

export default () => {}

export function ResultsQueriesList() {
  usePageView({ page_detail: 'DMConsole:ResultsQueriesList' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.resultsQueriesList'))
  const { datsJobState, datsJobActions } = useContext(DatsJobBaseContext)
  const { resultsQueriesListState, resultsQueriesListActions } = useResultsQueriesList(datsJobState.jobId)
  const { createResultQueryState, createResultQueryActions } = useCreateResultQuery(
    resultsQueriesListActions.fetchResultsQueries
  )

  useEffect(() => {
    resultsQueriesListActions.fetchResultsQueries()
    datsJobActions.clearRefreshObserver()
    datsJobActions.addRefreshObserver(resultsQueriesListActions.fetchResultsQueries)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!resultsQueriesListState) {
    return null
  }

  return (
    <>
      <Separator size="xxs" />
      <ToolsButtons datsJobActions={datsJobActions}>
        <Button onClick={createResultQueryActions.openDialog}>
          {t('dats.createResultQuery', 'Create Result Query')}
        </Button>
      </ToolsButtons>

      <CreateResultQuery
        createResultQueryState={createResultQueryState}
        createResultQueryActions={createResultQueryActions}
        datsJobActions={datsJobActions}
      />

      <DmcResponsiveTable
        separator={<br />}
        totalColumnCount={3}
        atSizeColumns={{
          xxl: {
            labels: [`${t('date', 'Date')}`, `${t('summary', 'Summary')}`, `${t('operations', 'Operations')}`],
            columnWidths: [2, 8, 2],
          },
          lg: {
            labels: [`${t('summary', 'Summary')}`, `${t('operations', 'Operations')}`],
            columnWidths: [9, 3],
            combinationRules: [['column1', 'column2'], ['column3']],
          },
          default: {
            labels: [`${t('dats.resultsQueries', 'Results Queries')}`],
            columnWidths: [12],
            combinationRules: [['column1', 'column2', 'column3']],
          },
        }}
      >
        {resultsQueriesListState.resultsQueries.map((resultsQuery) => {
          return (
            <ResponsiveTableRow
              key={v4()}
              name={`results_query_${resultsQuery._links.self.href.split('/')[4]}`}
              column1={convertUTCString(resultsQuery._attributes.modified, true)}
              column2={
                <ResultsQuerySummary
                  resultsQuery={resultsQuery}
                  resultsQueriesListActions={resultsQueriesListActions}
                />
              }
              column3={
                <ResultsQueryOperations
                  resultsQuery={resultsQuery}
                  resultsQueriesListActions={resultsQueriesListActions}
                />
              }
            />
          )
        })}
      </DmcResponsiveTable>
    </>
  )
}

const bold = css`
  font-weight: bold;
`

function ResultsQuerySummary({ resultsQuery, resultsQueriesListActions }) {
  const { t } = useTranslation()
  const [user, setUser] = useState(null)
  if (!user && resultsQuery?.userId) {
    getUserSummary(resultsQuery.userId, CIS_USER_ID_CONTEXT)
      .then((userResult) => {
        setUser(userResult.data)
      })
      .catch(() => {})
  }

  const { columns, rows } = resultsQueriesListActions.formatColumnsAndRowsForUI(resultsQuery)

  const rowsSection = rows?.contain?.map((row) => {
    return (
      <span key={v4()}>
        <span>{t(`dats.parameterName.${row.column}`, `${row.column}`)}</span>
        <span css={bold}> {t('dats.contains', 'contains')} </span>
        <span>{row.value}</span>
      </span>
    )
  })

  return (
    <>
      <DataBlock
        inline
        heading={t('dats.createdBy', 'Created By:')}
        data={(user && <UserLink showId userId={user.contributorId} userName={`${user.dmcUser.displayName}`} />) || ''}
      />
      <DataBlock inline heading={t('dats.queryId', 'Query ID:')} data={resultsQuery._links.self.href.split('/')[4]} />
      <DataBlock inline heading={t('dats.fileStatus', 'File Status:')} data={resultsQuery.queryProcessingState} />
      <DataBlock inline heading={t('dats.selected', 'Selected:')} data={rows.endResult} />
      {columns.include?.length > 0 && (
        <DataBlock
          inline
          heading={`${t('dats.columns', 'Columns')} ${t('dats.include', 'Include:')}`}
          data={columns.include.join(', ')}
        />
      )}
      {columns.exclude?.length > 0 && (
        <DataBlock
          inline
          heading={`${t('dats.columns', 'Columns')} t('dats.exclude', 'Exclude:')}`}
          data={columns.exclude.join(', ')}
        />
      )}
      {rows?.contain?.length > 0 && <DataBlock inline heading={t('filters.label', 'Filters')} data={rowsSection} />}
      {rows.noContain?.length > 0 &&
        rows.noContain.map((row) => {
          return (
            <DataBlock
              key={v4()}
              inline
              data={
                <span>
                  {row.column}
                  <span css={bold}> {t('dats.noContains', "doesn't contain")} </span>
                  {row.value}
                </span>
              }
            />
          )
        })}
    </>
  )
}

function ResultsQueryOperations({ resultsQuery, resultsQueriesListActions }) {
  const { t } = useTranslation()

  return (
    <>
      {resultsQuery._links?.file && (
        // The download attribute does not name the file. The server needs to send the header "Content-Disposition"
        // See https://stackoverflow.com/questions/23872902/chrome-download-attribute-not-working
        <>
          <Button to={`${getBaseUrl()}${resultsQuery._links?.file?.href || ''}?sessionId=${getSessionId()}`} download>
            {t('dats.downloadFile', 'Download File')}
          </Button>{' '}
          <br />
        </>
      )}
      {resultsQuery._links?.generate && (
        <>
          <Button
            onClick={() =>
              resultsQueriesListActions.hitEndpoint(resultsQuery._links?.generate.href, 'PUT', 'SingleCompressed')
            }
          >
            {t('dats.generateFile', 'Generate File')}
          </Button>
          <br />
        </>
      )}
      {resultsQuery._links?.file && (
        <>
          <DangerButton onClick={() => resultsQueriesListActions.hitEndpoint(resultsQuery._links?.file.href, 'DELETE')}>
            {t('dats.deleteFile', 'Delete File')}
          </DangerButton>
          <br />
        </>
      )}
      {resultsQuery._links?.self && (
        <>
          <Separator size="xxs" />
          <DangerButton
            size="xxs"
            onClick={() => resultsQueriesListActions.hitEndpoint(resultsQuery._links?.self.href, 'DELETE')}
          >
            {t('dats.deleteResultsQuery', 'Delete Results Query')}
          </DangerButton>
        </>
      )}
    </>
  )
}

/* eslint-enable no-underscore-dangle */
/* eslint-enable react/no-array-index-key */
